import React, { useEffect, useRef, useState } from 'react';

interface TocItem {
  id: string;
  label: string;
}

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/**
 * Document layout for legal / account pages.
 * Builds an "On this page" menu from the document's existing <h2> headings,
 * so the page content itself stays untouched.
 */
const LegalLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const docRef = useRef<HTMLElement>(null);
  const [toc, setToc] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const doc = docRef.current;
    if (!doc) return;

    const headings = Array.from(doc.querySelectorAll('h2'));
    const used = new Set<string>();
    const items = headings.map((heading, index) => {
      let id = heading.id || slugify(heading.textContent || '') || `section-${index + 1}`;
      while (used.has(id)) id = `${id}-${index + 1}`;
      used.add(id);
      heading.id = id;
      return { id, label: heading.textContent || '' };
    });
    setToc(items);

    // Highlight the section currently being read
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-90px 0px -70% 0px' }
    );
    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, []);

  const tocLinks = toc.map((item) => (
    <li key={item.id}>
      <a href={`#${item.id}`} className={item.id === activeId ? 'active' : ''}>
        {item.label}
      </a>
    </li>
  ));

  return (
    <section className="legal-v2">
      <div className="container">
        <div className={`legal-v2-grid${toc.length ? '' : ' legal-v2-grid-single'}`}>
          {toc.length > 0 && (
            <aside className="legal-v2-toc" aria-label="On this page">
              <span className="legal-v2-toc-title">On this page</span>
              <ol>{tocLinks}</ol>
            </aside>
          )}

          <div className="legal-v2-main">
            {toc.length > 0 && (
              <details className="legal-v2-toc-mobile">
                <summary>
                  On this page
                  <i className="fas fa-chevron-down" aria-hidden="true"></i>
                </summary>
                <ol>{tocLinks}</ol>
              </details>
            )}
            <article className="legal-v2-doc" ref={docRef}>
              {children}
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LegalLayout;
